/**
 * 查新 AI 搜索统一 API 接口定义
 * 对应 Python 服务 novelty_controller 中的各接口
 */

import type {
    SelectedComponent,
    AsyncTaskData,
    ComponentBindLocateRequest,
    UpdateComponentBindLocateRequest,
    ComponentBindLocateResponseData,
} from './component-bindlocate-api';

// ==================== component_extract_bindlocate ====================

/**
 * 部件提取+定位组合接口 - 图片项
 */
export interface ComponentExtractBindLocateImageItem {
    /** 图片ID */
    image_id: string;
    /** 图片URL */
    url: string;
}

/**
 * 部件提取+定位组合接口 - 请求体
 */
export interface ComponentExtractBindLocateRequest {
    /** 任务ID */
    task_id: string;
    /** 专利ID */
    patent_id: string;
    /** 特征文本列表 */
    feature_texts: string[];
    /** 图片列表 */
    images: ComponentExtractBindLocateImageItem[];
    /** 语言 */
    language?: string;
    is_async: boolean;
}

/**
 * 部件提取结果中的单个特征
 */
export interface ExtractedFeatureItem {
    /** 特征文本 */
    feature_text: string;
    /** 提取出的部件列表 */
    components: string[];
}

/**
 * 部件提取结果
 */
export interface ComponentExtractResult {
    /** 特征列表 */
    features: ExtractedFeatureItem[];
}

/**
 * 定位结果
 */
export interface ComponentBindLocateResult {
    /** 选中的部件列表 */
    selected_components: SelectedComponent[];
    /** 最终使用的附图ID列表 */
    final_figs_used: string[];
}

/**
 * 部件提取+定位组合接口 - 响应数据
 */
export interface ComponentExtractBindLocateResponseData {
    /** 部件提取结果 */
    extract_result: ComponentExtractResult;
    /** 定位结果（提取无部件时为 null） */
    bindlocate_result: ComponentBindLocateResult | null;
    /** 裁剪白边后的图片映射（image_id → 签名URL，上传失败时为 null） */
    trimmed_images: Record<string, string | null>;
}

// ==================== API 接口 ====================

/**
 * 查新 AI 搜索统一 API
 */
export const NoveltyAiSearchApi = Symbol.for('NoveltyAiSearchApi');

export interface NoveltyAiSearchApi {
    /**
     * 部件提取 + 图片定位组合接口
     * @param data 请求数据
     * @returns 异步任务数据
     */
    componentExtractBindLocate(data: ComponentExtractBindLocateRequest): Promise<AsyncTaskData<ComponentExtractBindLocateResponseData>>;

    /**
     * 部件绑定定位
     * @param data 请求数据
     * @returns 异步任务数据
     */
    componentBindLocate(data: ComponentBindLocateRequest): Promise<AsyncTaskData<ComponentBindLocateResponseData>>;

    /**
     * 更新部件绑定定位（保存用户标注修改）
     * @param data 更新请求数据
     * @returns 更新后的定位结果
     */
    updateComponentBindLocate(data: UpdateComponentBindLocateRequest): Promise<ComponentBindLocateResponseData>;
}
